import { createUseStyles } from '@glu/theming';
import React, { useRef, useEffect, MutableRefObject, KeyboardEvent } from 'react';
import { CaretRightIcon } from '@glu/icons-react';
import { AccessibilityText } from '@glu/utilities-react';
import locale from '@glu/locale';
import classnames from 'classnames';
import Checkbox from './Checkbox/Checkbox';
import Header from './Header';
import HighlightText from './HighlightText';
import styles from './Option.styles';
import { DropdownProps, PanelOption } from '../Dropdown';
import { PanelState } from '../PanelContext';

const useStyles = createUseStyles(styles);

interface Props {
  dark?: boolean;
  /** The user's typed input which options are filtered by  */
  filterString: PanelState['filterString'];
  handleKeyDown?(event: KeyboardEvent<Element>, option: PanelOption, isCheckbox?: boolean): void;
  htmlId?: DropdownProps['htmlId'];
  isCategory?: boolean;
  isFocused?: boolean;
  isSelected?: boolean;
  /** UX setting to allow multiple selections */
  multiselect?: DropdownProps['multiselect'];
  onChange?(name: string, checked: boolean): void;
  onClick?(option: PanelOption, isCategory?: boolean): void;
  /** Object representing a single option */
  option: PanelOption;
  /** Function to assign focus based on disabled or focused state of an option. */
  setFocus?(
    option: PanelOption,
    focused: boolean,
    optionRef: MutableRefObject<HTMLDivElement | null>
  ): void;
  /** Display setting for representing parent options as flat headers */
  showGroups?: DropdownProps['showGroups'];
}

const Option = (props: Props) => {
  const {
    dark,
    filterString,
    handleKeyDown,
    htmlId,
    isCategory,
    isFocused,
    isSelected,
    multiselect,
    onChange,
    onClick,
    option,
    setFocus,
    showGroups,
  } = props;

  const optionRef = useRef(null);
  const classes = useStyles(props as any);
  const optionTabIndex = 0;

  useEffect(() => {
    if (isFocused) {
      setFocus(option, isFocused, optionRef);
    }
    // Dont add missing deps here! Creats max-depth exceeded error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, optionRef]);

  const optionComponent = multiselect ? (
    <div
      className={`
          ${classes.root}
          ${classes[`depth${option.depth}`]}
          ${dark ? classes.dark : ''}
          ${isSelected ? classes.selected : ''}
          ${option.disabled ? classes.disabled : ''}
          ${option.highlight ? classes.highlight : ''}
        `}
      data-qa={`option-wrapper-${option.id}`}
    >
      <div ref={optionRef}>
        <Checkbox
          checked={isSelected}
          classes={classes}
          dataQa={`option-checkbox-${option.id}`}
          disabled={option.disabled}
          htmlId={htmlId}
          labelText={<HighlightText string={option.name} toMatch={filterString} />}
          name={option.name}
          onChange={onChange}
          onKeyDown={(event) => {
            handleKeyDown(event, option, true);
          }}
        />
      </div>

      {isCategory && !showGroups && (
        <button
          aria-selected={isSelected}
          className={classes.categoryButton}
          data-qa={`option-button-${option.id}`}
          disabled={option.disabled}
          id={`${htmlId}-option-button-${option.id}`}
          onClick={() => {
            onClick(option, true);
          }}
          onKeyDown={(event) => {
            handleKeyDown(event, option, true);
          }}
          role="option"
          tabIndex={optionTabIndex}
          type="button"
        >
          <AccessibilityText>{`${option.name} ${locale.get('category')}`}</AccessibilityText>
          <CaretRightIcon className={classnames(classes.icon, { [classes.dark]: dark })} />
        </button>
      )}
    </div>
  ) : isCategory && showGroups ? (
    <Header depth={option.depth}>{option.name}</Header>
  ) : (
    <div
      className={classnames(classes.singleOptionWrapper, { [classes.dark]: dark })}
      data-qa={`option-wrapper-${option.id}`}
    >
      <button
        ref={optionRef}
        aria-selected={isSelected}
        className={classnames(
          classes.root,
          classes[`depth${option.depth}`],
          dark && classes.dark,
          isSelected && classes.selected,
          option.disabled && classes.disabled,
          option.highlight && classes.highlight
        )}
        data-qa={`option-button-${option.id}`}
        disabled={option.disabled}
        id={`${htmlId}-category-option-button-${option.id}`}
        onClick={() => {
          onClick(option, false);
        }}
        onKeyDown={(event) => {
          handleKeyDown(event, option);
        }}
        role="option"
        tabIndex={optionTabIndex}
        title={option.name}
        type="button"
      >
        <HighlightText string={option.name} toMatch={filterString} />
      </button>
      {isCategory && !showGroups && (
        <button
          aria-selected={isSelected}
          className={classes.categoryButton}
          data-qa={`option-category-button-${option.id}`}
          disabled={option.disabled}
          id={`${htmlId}-option-button-${option.id}`}
          onClick={() => {
            onClick(option, true);
          }}
          onKeyDown={(event) => {
            handleKeyDown(event, option, true);
          }}
          role="option"
          tabIndex={optionTabIndex}
          type="button"
        >
          <AccessibilityText>{`${option.name} ${locale.get('category')}`}</AccessibilityText>
          <CaretRightIcon className={classnames(classes.icon, { [classes.dark]: dark })} />
        </button>
      )}
    </div>
  );

  return optionComponent;
};

export default Option;
